"use client";

import * as React from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { RANGES } from "@/components/time-range-selector";
import type { LoginDailyCount } from "@/providers/admin.provider";

const chartConfig = {
  count: {
    label: "Logins",
    color: "var(--primary)",
  },
} satisfies ChartConfig;

const DAY_MS = 24 * 60 * 60 * 1000;

const compactNumber = new Intl.NumberFormat("en-US", { notation: "compact" });

function toDateKey(ms: number) {
  return new Date(ms).toISOString().slice(0, 10);
}

// The API omits zero-login days, so fill the gaps to keep the time axis even.
// `days` on the API is a rolling window (now - N days), which also returns a
// clipped oldest day. Starting at today - (N - 1) drops it: "7 days" is 7 bars,
// with no artificial dip on the left.
function fillDailyLogins(sparse: LoginDailyCount[], days: number) {
  const byDate = new Map(sparse.map((d) => [d.date, d.count]));
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

  const filled: { date: string; count: number }[] = [];
  for (let ms = today - (days - 1) * DAY_MS; ms <= today; ms += DAY_MS) {
    const key = toDateKey(ms);
    filled.push({ date: key, count: byDate.get(key) ?? 0 });
  }

  return filled;
}

// Date-only keys parse as UTC midnight, so format in UTC too — a local timezone
// would label every bar a day early anywhere west of Greenwich.
function formatDate(date: string, options: Intl.DateTimeFormatOptions) {
  return new Date(date).toLocaleDateString("en-US", {
    ...options,
    timeZone: "UTC",
  });
}

interface Props {
  data: LoginDailyCount[];
  days: number;
  isLoading: boolean;
  isError?: boolean;
}

export function ChartLoginsPerDay({
  data,
  days,
  isLoading,
  isError = false,
}: Props) {
  const dailyData = React.useMemo(
    () => fillDailyLogins(data, days),
    [data, days],
  );

  const totalLogins = React.useMemo(
    () => dailyData.reduce((sum, d) => sum + d.count, 0),
    [dailyData],
  );

  const rangeLabel =
    RANGES.find((r) => r.value === String(days))?.label ?? `${days} days`;

  return (
    <Card className="@container/card">
      <CardHeader>
        <div className="flex min-w-0 flex-col gap-1">
          <CardTitle>Logins per day (UTC)</CardTitle>
          <CardDescription>
            {isLoading
              ? "Loading…"
              : isError
                ? "Couldn't load logins"
                : `${totalLogins.toLocaleString()} logins — ${rangeLabel.toLowerCase()}`}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {isLoading ? (
          <Skeleton className="aspect-auto h-[200px] w-full rounded-lg" />
        ) : isError ? (
          <div className="flex h-[200px] w-full items-center justify-center text-sm text-muted-foreground">
            Login data is unavailable right now.
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[200px] w-full"
          >
            <BarChart data={dailyData}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={24}
                tickFormatter={(date) =>
                  formatDate(String(date), { month: "short", day: "numeric" })
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
                width={40}
                tickFormatter={(value) => compactNumber.format(Number(value))}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(date) =>
                      formatDate(String(date), {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    }
                    indicator="dot"
                  />
                }
              />
              <Bar dataKey="count" fill="var(--color-count)" radius={2} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
